import { API_BASE, api, ApiError } from "@/lib/apiClient";
import { getOrCreateInfoScreenDeviceToken } from "@/lib/infoScreenDeviceStorage";
import { guestTabletPublicFetch } from "@/lib/guestTabletPublicFetch";
import { tenantSlug } from "@/lib/tenantSlug";
import type {
  InfoScreenContent,
  InfoScreenDevice,
  InfoScreenDeviceStatus,
  InfoScreenDisplay,
  InfoScreenDisplayPayload,
} from "@/types/infoScreen";

const TENANT_HEADER = "X-Tenant-Slug";
export const INFO_SCREEN_DEVICE_HEADER = "X-Info-Screen-Device-Token";

function publicHeaders(extra?: HeadersInit): HeadersInit {
  const token = getOrCreateInfoScreenDeviceToken();
  return {
    [TENANT_HEADER]: tenantSlug(),
    Accept: "application/json",
    [INFO_SCREEN_DEVICE_HEADER]: token,
    ...(extra as Record<string, string> | undefined),
  };
}

function buildPublicUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await guestTabletPublicFetch(buildPublicUrl, publicHeaders, path, init);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    const message =
      err instanceof Error &&
      (err.message === "Load failed" || err.message === "Failed to fetch")
        ? "Verbindung zum Server fehlgeschlagen."
        : err instanceof Error
          ? err.message
          : "Netzwerkfehler";
    throw new ApiError(0, message);
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: string };
      if (typeof parsed.detail === "string") message = parsed.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(
      res.status,
      "Ungültige Server-Antwort. Bitte prüfen Sie, ob die API erreichbar ist.",
    );
  }
}

export async function fetchInfoScreenDisplay(
  displayCode: string,
): Promise<InfoScreenDisplayPayload> {
  return publicFetch<InfoScreenDisplayPayload>(
    `/api/v1/public/info-screen/display/${encodeURIComponent(displayCode)}`,
  );
}

/** SSE-URL für Live-Updates (EventSource unterstützt keine Custom-Header). */
export function infoScreenStreamUrl(displayCode: string): string {
  const token = encodeURIComponent(getOrCreateInfoScreenDeviceToken());
  const path = `/api/v1/public/info-screen/stream/${encodeURIComponent(displayCode)}?device_token=${token}`;
  return buildPublicUrl(path);
}

/** SSE-URL für Geräte-Freischaltung (EventSource unterstützt keine Custom-Header). */
export function infoScreenDeviceStreamUrl(displayCode: string): string {
  const token = encodeURIComponent(getOrCreateInfoScreenDeviceToken());
  const path = `/api/v1/public/info-screen/devices/stream?display_code=${encodeURIComponent(displayCode)}&device_token=${token}`;
  return buildPublicUrl(path);
}

export async function registerInfoScreenDevice(
  displayCode: string,
): Promise<InfoScreenDeviceStatus> {
  const device_token = getOrCreateInfoScreenDeviceToken();
  return publicFetch<InfoScreenDeviceStatus>("/api/v1/public/info-screen/devices/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      display_code: displayCode,
      device_token,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    }),
  });
}

export async function fetchInfoScreenDeviceStatus(
  displayCode: string,
): Promise<InfoScreenDeviceStatus> {
  return publicFetch<InfoScreenDeviceStatus>(
    `/api/v1/public/info-screen/devices/status?display_code=${encodeURIComponent(displayCode)}`,
  );
}

export async function fetchInfoScreenDisplays(): Promise<InfoScreenDisplay[]> {
  return api.get<InfoScreenDisplay[]>("/api/v1/info-screen/displays");
}

export async function createInfoScreenDisplay(body: {
  code: string;
  label: string;
  sort_order?: number;
}): Promise<InfoScreenDisplay> {
  return api.post<InfoScreenDisplay>("/api/v1/info-screen/displays", body);
}

export async function updateInfoScreenDisplay(
  displayId: string,
  body: { label?: string; is_active?: boolean; sort_order?: number },
): Promise<InfoScreenDisplay> {
  return api.patch<InfoScreenDisplay>(`/api/v1/info-screen/displays/${displayId}`, body);
}

export async function deactivateInfoScreenDisplay(
  displayId: string,
): Promise<InfoScreenDisplay> {
  return api.del<InfoScreenDisplay>(`/api/v1/info-screen/displays/${displayId}`);
}

export async function fetchInfoScreenContent(): Promise<InfoScreenContent> {
  return api.get<InfoScreenContent>("/api/v1/info-screen/content");
}

export async function updateInfoScreenContent(
  body: Partial<InfoScreenContent>,
): Promise<InfoScreenContent> {
  return api.patch<InfoScreenContent>("/api/v1/info-screen/content", body);
}

export async function approveInfoScreenDevice(deviceId: string): Promise<InfoScreenDevice> {
  return api.post<InfoScreenDevice>(`/api/v1/info-screen/devices/${deviceId}/approve`, {});
}

export async function revokeInfoScreenDevice(deviceId: string): Promise<InfoScreenDevice> {
  return api.post<InfoScreenDevice>(`/api/v1/info-screen/devices/${deviceId}/revoke`, {});
}

export async function updateInfoScreenDeviceLabel(
  deviceId: string,
  label: string | null,
): Promise<InfoScreenDevice> {
  return api.patch<InfoScreenDevice>(`/api/v1/info-screen/devices/${deviceId}`, { label });
}

export async function deleteInfoScreenDevice(deviceId: string): Promise<void> {
  return api.del(`/api/v1/info-screen/devices/${deviceId}`);
}
